import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Layout } from '@/components/layout/Layout';
import { DayView } from '@/components/calendar/DayView';
import { WeekView } from '@/components/calendar/WeekView';
import { MonthView } from '@/components/calendar/MonthView';
import { KanbanView } from '@/components/kanban/KanbanView';
import { TableView } from '@/components/table/TableView';
import { ScissorPage } from '@/pages/ScissorPage';
import { Settings } from '@/pages/Settings';
import { Analytics } from '@/pages/Analytics';
import { StatePage } from '@/pages/StatePage';
import { WorkspacePage } from '@/pages/WorkspacePage';
import { UnscheduledPage } from '@/pages/UnscheduledPage';
import { TeamsPage } from '@/pages/TeamsPage';
import { HelpPage } from '@/pages/HelpPage';
import { CalendarConnected } from '@/pages/CalendarConnected';
import { AuthPage } from '@/pages/AuthPage';
import { Onboarding } from '@/pages/Onboarding';
import { CommandPalette } from '@/components/command/CommandPalette';
import { ImmersionMode } from '@/components/immersion/ImmersionMode';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useTasksStore } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useUserStore } from '@/stores/userStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { api } from '@/lib/api';
import { taskToUI } from '@flexiplanner/types';
import { Button } from '@/components/ui/button';
import { TimerReset } from 'lucide-react';

function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();
  const onboardingCompleted = useUserStore(s => s.onboardingCompleted);
  const setTasks = useTasksStore(s => s.setTasks);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  const isCommandPaletteOpen = useScheduleStore(s => s.isCommandPaletteOpen);
  const toggleCommandPalette = useScheduleStore(s => s.toggleCommandPalette);
  const setCommandPaletteOpen = useScheduleStore(s => s.setCommandPaletteOpen);
  const activeImmersionTaskIds = useScheduleStore(s => s.activeImmersionTaskIds);
  const minimizedImmersionTaskIds = useScheduleStore(s => s.minimizedImmersionTaskIds);
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const endImmersionMode = useScheduleStore(s => s.endImmersionMode);
  const minimizeImmersionMode = useScheduleStore(s => s.minimizeImmersionMode);
  const restoreImmersionMode = useScheduleStore(s => s.restoreImmersionMode);
  const tasks = useTasksStore(s => s.tasks);
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);

  const immersionTasks = activeImmersionTaskIds
    .filter(taskId => !minimizedImmersionTaskIds.includes(taskId))
    .map(taskId => tasks.find(t => t.id === taskId))
    .filter(Boolean);
  const minimizedImmersionTasks = minimizedImmersionTaskIds
    .map(taskId => tasks.find(t => t.id === taskId))
    .filter(Boolean);

  // Initialize WebSocket connection
  useWebSocket();

  // Initialize keyboard shortcuts
  useKeyboardShortcuts({
    'cmd+k': () => toggleCommandPalette(),
    'ctrl+k': () => toggleCommandPalette(),
  });

  // Load tasks and schedule on mount
  useEffect(() => {
    if (isAuthenticated && onboardingCompleted) {
      loadCurrentPlan();
      loadWorkspace();
      loadActiveImmersion();
    }
  }, [isAuthenticated, onboardingCompleted, loadWorkspace]);

  const loadCurrentPlan = async () => {
    try {
      await api.schedule.rollover();
    } catch (error) {
      console.error('Failed to replan missed activities:', error);
    }

    await Promise.all([loadTasks(), loadSchedule()]);
  };

  const loadActiveImmersion = async () => {
    try {
      const response = await api.training.getActiveImmersions();
      if (response.success && Array.isArray(response.data)) {
        response.data.forEach((session: any) => {
          if (session.task_id) startImmersionMode(session.task_id);
        });
      }
    } catch (error) {
      console.error('Failed to load active immersion:', error);
    }
  };

  const loadTasks = async () => {
    try {
      const response = await api.tasks.list();
      if (response.success && response.data) {
        // Handle both format variations just in case
        const tasksList = Array.isArray(response.data)
          ? response.data
          : (response.data as any)?.tasks;
        if (tasksList) {
          const uiTasks = tasksList.map(taskToUI);
          setTasks(uiTasks);
        }
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const loadSchedule = async () => {
    try {
      const response = await api.schedule.getGhostBlocks();
      if (response.success && response.data) {
        setBlocks(normalizeScheduleBlocks(response.data));
        setGhostBlocks(normalizeGhostBlocks(response.data));
        // Set calendar events from the response
        if (response.data.gcal_events) {
          setCalendarEvents(normalizeCalendarEvents(response.data.gcal_events));
        }
      }
    } catch (error) {
      console.error('Failed to load schedule:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2"></div>
          <p className="text-muted-foreground">Loading Fluxiplanner...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <AuthPage />
        <Toaster />
      </>
    );
  }

  if (!onboardingCompleted) {
    return (
      <>
        <Onboarding />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/week" replace />} />
          <Route path="/day" element={<DayView />} />
          <Route path="/week" element={<WeekView />} />
          <Route path="/month" element={<MonthView />} />
          <Route path="/kanban" element={<KanbanView />} />
          <Route path="/table" element={<TableView />} />
          <Route path="/scissor" element={<ScissorPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/state" element={<StatePage />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/teams/:teamId" element={<TeamsPage />} />
          <Route path="/teams/:teamId/:section" element={<TeamsPage />} />
          <Route path="/unscheduled" element={<UnscheduledPage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/calendar/connected" element={<CalendarConnected />} />
        </Routes>
      </Layout>
      <CommandPalette open={isCommandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
      {immersionTasks.length > 0 && (
        <div className="native-scroll fixed inset-0 z-50 flex flex-col gap-3 overflow-y-auto bg-background/78 px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-[calc(1rem+env(safe-area-inset-top))] backdrop-blur-xl md:inset-x-auto md:bottom-4 md:right-4 md:top-auto md:max-h-[calc(100dvh-2rem)] md:w-[420px] md:bg-transparent md:p-0 md:backdrop-blur-none">
          {immersionTasks.map(task => (
            <ImmersionMode
              key={task!.id}
              task={task!}
              onMinimize={() => minimizeImmersionMode(task!.id)}
              onClose={() => endImmersionMode(task!.id)}
              onComplete={() => endImmersionMode(task!.id)}
            />
          ))}
        </div>
      )}
      {minimizedImmersionTasks.length > 0 && (
        <div className="fixed inset-x-3 bottom-20 z-50 flex flex-wrap gap-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-[460px]">
          {minimizedImmersionTasks.map(task => (
            <Button
              key={task!.id}
              type="button"
              variant="secondary"
              className="min-h-10 max-w-full justify-start rounded-full shadow-lg"
              onClick={() => restoreImmersionMode(task!.id)}
              title={`Restore ${task!.title}`}
            >
              <TimerReset className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">Session: {task!.title}</span>
            </Button>
          ))}
        </div>
      )}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function normalizeScheduleBlocks(data: any) {
  const ghostBlocks = (data.ghost_blocks || []).map((block: any) => ({
    id: block.id,
    taskId: block.task_id,
    title: block.title || 'Suggested activity',
    startTime: block.proposed_start,
    endTime: block.proposed_end,
    isGhost: true,
  }));

  const workSegments = (data.work_segments || []).map((segment: any) => ({
    id: segment.id,
    taskId: segment.task_id,
    title: segment.segment_type === 'pause' ? 'Free time' : segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: segment.segment_type === 'pause' ? 5 : 3,
    requiredEnergy: segment.segment_type === 'pause' ? 0 : undefined,
  }));

  return [...ghostBlocks, ...workSegments];
}

function normalizeGhostBlocks(data: any) {
  return (data.ghost_blocks || []).map((block: any) => ({
    id: block.id,
    taskId: block.task_id,
    proposedStart: block.proposed_start,
    proposedEnd: block.proposed_end,
    cost: block.cost,
    confidence: block.confidence,
    reason: block.reason,
  }));
}

function normalizeCalendarEvents(events: any[] = []) {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time,
      end: event.end || event.end_time,
      isImmovable: Boolean(event.is_immovable ?? true),
      isFlexHold: Boolean(event.is_flex_hold),
      stateProfile: event.state_profile || null,
      customAttributes: parseCustomAttributes(event.custom_attributes),
    }))
    .filter(isRenderableCalendarEvent);
}

function isRenderableCalendarEvent(event: {
  start?: string;
  end?: string;
  isFlexHold?: boolean;
  customAttributes?: Record<string, unknown>;
}): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return source !== 'canvas' && source !== 'book-tracker' && !attributes.canvas && !attributes.book_tracker;
}

function isAllDayCalendarEvent(event: { start?: string; end?: string }): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export default App;
